<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  IconCamera,
  IconCirclePlus,
  IconFile,
  IconMicrophone,
  IconPencil,
  IconPhoto,
  IconTextScan2,
} from '@tabler/icons-vue'

const emit = defineEmits<{
  (e: 'manual'): void
  (e: 'images-selected', files: File[]): void
}>()

const route = useRoute()
const showMenu = ref(false)
const cameraInput = ref<HTMLInputElement | null>(null)
const photosInput = ref<HTMLInputElement | null>(null)
const filesInput = ref<HTMLInputElement | null>(null)
const audioInput = ref<HTMLInputElement | null>(null)

const attachmentActions = [
  { id: 'camera', label: 'Camera', icon: IconCamera },
  { id: 'photos', label: 'Photos', icon: IconPhoto },
  { id: 'files', label: 'Files', icon: IconFile },
  { id: 'audio', label: 'Audio', icon: IconMicrophone },
  { id: 'scan', label: 'Scan', icon: IconTextScan2 },
  { id: 'manual', label: 'Create Manually', icon: IconPencil },
]

function toggleMenu() {
  showMenu.value = !showMenu.value
}

function closeMenu() {
  showMenu.value = false
}

function handleAction(actionId: string) {
  closeMenu()

  if (actionId === 'camera') {
    cameraInput.value?.click()
    return
  }

  if (actionId === 'photos') {
    photosInput.value?.click()
    return
  }

  if (actionId === 'files') {
    filesInput.value?.click()
    return
  }

  if (actionId === 'audio') {
    audioInput.value?.click()
    return
  }

  emit('manual')
}

function clearSelectedFiles(event: Event) {
  const target = event.target as HTMLInputElement
  target.value = ''
}

function handleImageInputChange(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files ? Array.from(target.files) : []

  if (files.length > 0) {
    emit('images-selected', files)
  }

  target.value = ''
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

watch(
  () => route.path,
  () => {
    closeMenu()
  },
)

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div class="relative">
    <button
      v-if="showMenu"
      class="fixed inset-0 z-30 bg-transparent"
      type="button"
      aria-label="Close attachment menu"
      @click="closeMenu"
    />
    <button
      type="button"
      class="relative z-40 text-base-content h-full flex items-center justify-center"
      aria-label="Open attachment menu"
      aria-haspopup="menu"
      :aria-expanded="showMenu"
      @click="toggleMenu"
    >
      <IconCirclePlus
        id="circle-plus-icon"
        class="h-9 w-9 transition-transform duration-200"
        :class="{ 'rotate-45': showMenu }"
        :stroke-width="1.3"
      />
    </button>
    <transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-1 scale-95"
    >
      <div
        v-if="showMenu"
        class="absolute bottom-12 left-0 z-50 w-56 rounded-2xl border border-black/10 bg-base-100 p-2 shadow-xl"
        role="menu"
        aria-label="Attachment options"
      >
        <button
          v-for="action in attachmentActions"
          :key="action.id"
          type="button"
          class="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left text-base-content transition-colors hover:bg-base-200/80"
          role="menuitem"
          @click="handleAction(action.id)"
        >
          <span
            class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white"
          >
            <component :is="action.icon" :size="20" />
          </span>
          <span class="text-lg leading-none">{{ action.label }}</span>
        </button>
      </div>
    </transition>
    <input
      ref="cameraInput"
      type="file"
      accept="image/*"
      capture="environment"
      class="hidden"
      @change="handleImageInputChange"
    />
    <input
      ref="photosInput"
      type="file"
      accept="image/*"
      multiple
      class="hidden"
      @change="handleImageInputChange"
    />
    <input ref="filesInput" type="file" class="hidden" @change="clearSelectedFiles" />
    <input
      ref="audioInput"
      type="file"
      accept="audio/*"
      class="hidden"
      @change="clearSelectedFiles"
    />
  </div>
</template>

<style scoped></style>
